<template>
  <Dialog
    v-model:visible="visibleModel"
    :style="{ width: '650px' }"
    header="Email Schedule"
    :modal="true"
  >
    <div class="email-form">
      <!-- Recipients Section -->
      <div class="form-group">
        <label class="section-label">Recipients</label>
        <div class="recipients-actions">
          <Button
            label="Select All"
            icon="pi pi-check"
            @click="selectAll"
            class="p-button-text p-button-sm"
          />
          <Button
            label="Select None"
            icon="pi pi-times"
            @click="selectNone"
            class="p-button-text p-button-sm"
          />
        </div>
        <div class="staff-list">
          <div
            v-for="staff in availableStaff"
            :key="staff.id"
            class="staff-option"
          >
            <Checkbox
              :binary="true"
              :modelValue="selectedStaffIds.includes(staff.id)"
              @update:modelValue="(checked) => toggleStaff(staff.id, checked)"
            />
            <label class="staff-label">
              {{ staff.name }}
              <span class="staff-email">{{ staff.email }}</span>
              <span v-if="staff.role === 'admin'" class="admin-badge">Admin</span>
            </label>
          </div>
          <div v-if="availableStaff.length === 0" class="no-staff-message">
            No staff members with email addresses found.
          </div>
        </div>
      </div>

      <!-- Subject Field -->
      <div class="form-group">
        <label>Subject</label>
        <InputText v-model="subject" class="w-full" placeholder="Email subject" />
      </div>

      <!-- Rich Text Editor -->
      <div class="form-group">
        <label>Message</label>
        <div class="editor-container">
          <!-- Toolbar -->
          <div class="editor-toolbar" v-if="editor">
            <button
              type="button"
              @click="editor.chain().focus().toggleBold().run()"
              :class="{ 'is-active': editor.isActive('bold') }"
              class="toolbar-button"
              title="Bold"
            >
              <span class="toolbar-icon bold-icon">B</span>
            </button>
            <button
              type="button"
              @click="editor.chain().focus().toggleItalic().run()"
              :class="{ 'is-active': editor.isActive('italic') }"
              class="toolbar-button"
              title="Italic"
            >
              <span class="toolbar-icon italic-icon">I</span>
            </button>
            <button
              type="button"
              @click="editor.chain().focus().toggleUnderline().run()"
              :class="{ 'is-active': editor.isActive('underline') }"
              class="toolbar-button"
              title="Underline"
            >
              <span class="toolbar-icon underline-icon">U</span>
            </button>
            <span class="toolbar-divider"></span>
            <button
              type="button"
              @click="editor.chain().focus().toggleBulletList().run()"
              :class="{ 'is-active': editor.isActive('bulletList') }"
              class="toolbar-button"
              title="Bullet List"
            >
              <i class="pi pi-list"></i>
            </button>
            <button
              type="button"
              @click="editor.chain().focus().toggleOrderedList().run()"
              :class="{ 'is-active': editor.isActive('orderedList') }"
              class="toolbar-button"
              title="Ordered List"
            >
              <span class="toolbar-icon">1.</span>
            </button>
          </div>
          <!-- Editor Content -->
          <EditorContent :editor="editor" class="editor-content" />
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        icon="pi pi-times"
        @click="onCancel"
        class="p-button-text"
      />
      <Button
        label="Send"
        icon="pi pi-send"
        @click="onSend"
        :disabled="!canSend"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { useCalendarEmail } from '@/composables/useCalendarEmail';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  store: {
    type: Object,
    required: true,
  },
  staffMembers: {
    type: Array,
    default: () => [],
  },
  calendarApi: {
    type: Object,
    default: null,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:visible', 'send', 'cancel']);

const { generateDefaultSubject } = useCalendarEmail();

// Two-way binding for visible
const visibleModel = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

// Selected staff IDs
const selectedStaffIds = ref([]);

// Subject field
const subject = ref('');

// Rich text editor
const editor = useEditor({
  extensions: [
    StarterKit,
    Underline,
  ],
  content: '<p></p>',
  editorProps: {
    attributes: {
      class: 'tiptap-editor',
    },
  },
});

// Filter staff members with email addresses
const availableStaff = computed(() => {
  const staffWithEmail = props.staffMembers.filter(staff => staff.email && staff.email.trim() !== '');

  // For non-admins, only show themselves
  if (!props.isAdmin) {
    // Find current user in staff list (would need to be passed or from auth store)
    // For now, we'll return empty or filter by current user ID if available
    // This assumes the parent component filters the list appropriately
    return staffWithEmail;
  }

  return staffWithEmail;
});

// Initialize default selections when dialog opens
watch(() => props.visible, (newVisible) => {
  if (newVisible) {
    // Reset selections and generate default subject
    subject.value = generateDefaultSubject(props.store, props.calendarApi);

    // Pre-select admins by default
    selectedStaffIds.value = availableStaff.value
      .filter(staff => staff.role === 'admin')
      .map(staff => staff.id);

    // Reset editor content
    if (editor.value) {
      editor.value.commands.setContent('<p></p>');
    }
  }
});

// Toggle staff selection
const toggleStaff = (staffId, checked) => {
  if (checked) {
    if (!selectedStaffIds.value.includes(staffId)) {
      selectedStaffIds.value.push(staffId);
    }
  } else {
    selectedStaffIds.value = selectedStaffIds.value.filter(id => id !== staffId);
  }
};

// Select all staff
const selectAll = () => {
  selectedStaffIds.value = availableStaff.value.map(staff => staff.id);
};

// Select none
const selectNone = () => {
  selectedStaffIds.value = [];
};

// Validation - message is optional
const canSend = computed(() => {
  return (
    selectedStaffIds.value.length > 0 &&
    subject.value.trim() !== ''
  );
});

// Handle send
const onSend = () => {
  if (!canSend.value) return;

  const recipients = selectedStaffIds.value
    .map(id => availableStaff.value.find(staff => staff.id === id))
    .filter(staff => staff && staff.email)
    .map(staff => staff.email);

  const messageHtml = editor.value.getHTML();

  emit('send', {
    recipients,
    subject: subject.value,
    messageHtml,
  });
};

// Handle cancel
const onCancel = () => {
  emit('cancel');
};
</script>

<style scoped>
.email-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #333;
}

.section-label {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
}

.recipients-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.staff-list {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
  background-color: #fafafa;
}

.staff-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.staff-option:hover {
  background-color: #f0f0f0;
}

.staff-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: normal;
  flex: 1;
}

.staff-email {
  color: #6b7280;
  font-size: 0.875rem;
}

.admin-badge {
  background-color: #3b82f6;
  color: white;
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-weight: 600;
}

.no-staff-message {
  color: #6b7280;
  font-style: italic;
  padding: 1rem;
  text-align: center;
}

.w-full {
  width: 100%;
}

/* Editor Styles */
.editor-container {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  background-color: white;
}

.editor-toolbar {
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem;
  border-bottom: 1px solid #d1d5db;
  background-color: #f9fafb;
  flex-wrap: wrap;
}

.toolbar-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid transparent;
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
}

.toolbar-button:hover {
  background-color: #e5e7eb;
  border-color: #d1d5db;
}

.toolbar-button.is-active {
  background-color: #dbeafe;
  border-color: #93c5fd;
  color: #1e40af;
}

.toolbar-icon {
  font-size: 14px;
  font-family: Georgia, serif;
}

.bold-icon {
  font-weight: 700;
}

.italic-icon {
  font-style: italic;
}

.underline-icon {
  text-decoration: underline;
}

.toolbar-divider {
  width: 1px;
  background-color: #d1d5db;
  margin: 0 0.25rem;
}

.editor-content {
  padding: 0.75rem;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
}

/* Tiptap editor content styles */
.editor-content :deep(.tiptap-editor) {
  outline: none;
  min-height: 150px;
}

.editor-content :deep(.tiptap-editor p) {
  margin: 0 0 0.5rem 0;
}

.editor-content :deep(.tiptap-editor p:last-child) {
  margin-bottom: 0;
}

.editor-content :deep(.tiptap-editor ul),
.editor-content :deep(.tiptap-editor ol) {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

.editor-content :deep(.tiptap-editor li) {
  margin: 0.25rem 0;
}

.editor-content :deep(.tiptap-editor strong) {
  font-weight: 700;
}

.editor-content :deep(.tiptap-editor em) {
  font-style: italic;
}

.editor-content :deep(.tiptap-editor u) {
  text-decoration: underline;
}

/* Scrollbar styling */
.staff-list::-webkit-scrollbar,
.editor-content::-webkit-scrollbar {
  width: 8px;
}

.staff-list::-webkit-scrollbar-track,
.editor-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.staff-list::-webkit-scrollbar-thumb,
.editor-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.staff-list::-webkit-scrollbar-thumb:hover,
.editor-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
